import { createContext, forwardRef, useCallback, useContext, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { STATUS_LABEL, SWATCHES, type BookingStatus, type Channel } from "@shared/domain";
import { COUNTRIES, countryByIso, DEFAULT_COUNTRY, flagOf, splitPhone, type Country } from "@shared/phone";
import { swatch, useChannelLook } from "@/lib/colors";
import { addDays, fmtCountdown, fmtShort, weekdayShort } from "@shared/dates";
import { cn } from "@/lib/cn";
import { useNow } from "@/stores/feedback";
import { useInRail } from "./rail";
import { usePageMeta } from "@/stores/page";
import { AltArrowDownIcon, AltArrowLeftIcon, AltArrowRightIcon, MinusIcon, AddCircleIcon, CheckCircleBoldIcon, InboxIcon } from "./icons";
import { Calendar } from "./DatePicker";
import { FormErrorsContext } from "./formErrors";
import { finePointer, LAYER_ATTR, useLayer } from "./layer";
import { createPortal } from "react-dom";

/* ───────────────────────────── Buttons ───────────────────────────── */

type Variant = "primary" | "hold" | "confirm" | "secondary" | "outline" | "danger" | "danger-outline" | "ghost" | "dashed";
type Size = "sm" | "md" | "lg" | "xl";

const variants: Record<Variant, string> = {
  primary: "bg-deodar text-on-fill hover:brightness-110 font-bold",
  hold: "bg-brass text-brass-ink hover:brightness-105 font-bold",
  confirm: "bg-pine text-on-fill hover:brightness-110 font-bold",
  secondary: "bg-sunk border border-line-strong text-ink hover:bg-canvas font-semibold",
  outline: "bg-surface border border-ink text-ink hover:bg-sunk font-semibold",
  danger: "bg-rhodo text-[#FFFDF8] hover:brightness-110 font-bold",
  "danger-outline": "bg-transparent border border-rhodo text-rhodo hover:bg-rhodo/10 font-semibold",
  ghost: "bg-transparent text-muted hover:text-ink hover:bg-sunk font-semibold",
  dashed: "bg-sunk border border-dashed border-line-strong text-muted hover:text-ink font-semibold",
};
const sizes: Record<Size, string> = {
  sm: "min-h-[38px] rounded-sm px-3 text-[13px]",
  md: "min-h-[44px] rounded-md px-4 text-[14px]",
  lg: "min-h-[48px] rounded-md px-4 text-[15px]",
  xl: "min-h-[56px] rounded-lg px-5 text-[16px]",
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  loading?: boolean;
};

/**
 * Primary is the commit, and wears the kadil: a wider top-right corner. A
 * confirm that commits a booking can take it too, with `className="kadil"`.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon, loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 leading-tight transition-[filter,background-color,color] duration-100 select-none",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variants[variant],
        sizes[size],
        variant === "primary" && (size === "xl" ? "kadil-lg" : "kadil"),
        className,
      )}
      {...rest}
    >
      {loading ? <span className="size-2.5 rounded-[3px] bg-current animate-pulse-k" aria-hidden /> : icon}
      {children}
    </button>
  );
});

/** Print · PDF · Excel: flush buttons in one rounded group. */
export function ButtonGroup({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("inline-flex overflow-hidden rounded-md border border-line-strong [&>*]:rounded-none [&>*]:border-0 [&>*:not(:last-child)]:border-r [&>*:not(:last-child)]:border-line-strong", className)}>
      {children}
    </div>
  );
}

export function IconButton({ label, className, children, round, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; round?: boolean }) {
  return (
    <button
      aria-label={label}
      title={label}
      className={cn("inline-flex size-11 shrink-0 items-center justify-center border border-line-strong bg-sunk text-ink hover:bg-canvas", round ? "rounded-full" : "rounded-md", className)}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ───────────────────────────── Type ───────────────────────────── */

export function Eyebrow({ children, className, tone }: { children: ReactNode; className?: string; tone?: "rhodo" | "deodar" }) {
  return <div className={cn("eyebrow", tone === "rhodo" && "!text-rhodo", tone === "deodar" && "!text-deodar", className)}>{children}</div>;
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("font-mono", className)}>{children}</span>;
}

/* ───────────────────────────── Chips & pills ───────────────────────────── */

export function ChannelChip({ channel, size = "sm", className }: { channel: Channel | null; size?: "xs" | "sm" | "md"; className?: string }) {
  const { lookup } = useChannelLook();
  const meta = channel ? lookup(channel) : { short: "BLK", color: "var(--border-strong)", ink: "#FFFDF8", label: "Blocked" };
  return (
    <span
      title={meta.label}
      className={cn(
        "inline-flex shrink-0 items-center rounded-xs font-mono font-semibold tracking-[.06em]",
        size === "xs" && "px-1 py-px text-[9px]",
        size === "sm" && "h-[22px] px-[7px] text-[10.5px]",
        size === "md" && "h-[26px] px-2 text-[11px]",
        className,
      )}
      style={{ background: meta.color, color: meta.ink }}
    >
      {meta.short}
    </span>
  );
}

/** A picked colour as a small dot beside a name: a meal plan, an extra. Nothing when none was picked. */
export function ColorDot({ color, className }: { color: string | null | undefined; className?: string }) {
  if (!color) return null;
  return <span aria-hidden className={cn("inline-block size-2.5 shrink-0 rounded-full", className)} style={{ background: swatch(color) }} />;
}

/**
 * Pick a colour: the house swatches first, then any colour at all through the
 * system picker. `allowNone` adds a "none" choice (null) — for a meal plan
 * that needs no mark, or a built-in channel going back to its theme colour.
 */
export function ColorPicker({
  value,
  onChange,
  allowNone,
  noneLabel = "None",
  label,
}: {
  value: string | null;
  onChange: (c: string | null) => void;
  allowNone?: boolean;
  noneLabel?: string;
  label: string;
}) {
  const custom = value !== null && !(SWATCHES as readonly string[]).includes(value.toLowerCase());
  const ring = "shadow-[0_0_0_2px_var(--surface),0_0_0_4px_var(--ink)]";
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap items-center gap-2">
      {allowNone && (
        <button
          type="button"
          role="radio"
          aria-checked={value === null}
          onClick={() => onChange(null)}
          className={cn("h-7 rounded-full border border-line-strong bg-surface px-2.5 font-mono text-[10.5px] uppercase tracking-[.08em] text-muted", value === null && ring)}
        >
          {noneLabel}
        </button>
      )}
      {SWATCHES.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={value?.toLowerCase() === c}
          aria-label={c}
          title={c}
          onClick={() => onChange(c)}
          className={cn("size-7 rounded-full", value?.toLowerCase() === c && ring)}
          style={{ background: swatch(c) }}
        />
      ))}
      <label
        title="Any colour"
        className={cn("relative inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-full border border-line-strong bg-surface pl-1 pr-2.5 font-mono text-[10.5px] uppercase tracking-[.08em] text-muted", custom && ring)}
      >
        <span aria-hidden className="size-5 rounded-full border border-line" style={{ background: custom ? swatch(value) : "conic-gradient(#c8442f, #c98a1e, #3f5e43, #1f6e6a, #2e4e8f, #8e4470, #c8442f)" }} />
        {custom ? value : "Custom"}
        <input type="color" aria-label={`${label}: any colour`} className="absolute inset-0 cursor-pointer opacity-0" value={value ?? "#a0552b"} onChange={(e) => onChange(e.target.value.toLowerCase())} />
      </label>
    </div>
  );
}

export type PillTone = "pine" | "brass" | "rhodo" | "slate" | "deodar" | "subtle";
const pillColor: Record<PillTone, string> = {
  pine: "var(--pine)",
  brass: "var(--brass)",
  rhodo: "var(--rhododendron)",
  slate: "var(--slate)",
  deodar: "var(--deodar)",
  subtle: "var(--ink-subtle)",
};

/**
 * A tinted pill with a leading dot. The text is the tone mixed toward ink, so
 * brass stays readable on cream and lightens correctly at night.
 */
export function Pill({ tone = "subtle", dot = true, children, className }: { tone?: PillTone; dot?: boolean; children: ReactNode; className?: string }) {
  const c = pillColor[tone];
  return (
    <span
      className={cn("inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full pr-2.5 font-mono text-[10.5px] font-medium uppercase tracking-[.08em]", dot ? "pl-2" : "pl-2.5", className)}
      style={{ background: `color-mix(in srgb, ${c} 14%, var(--surface))`, color: `color-mix(in srgb, ${c} 78%, var(--ink))` }}
    >
      {dot && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/** "Repeat" and similar tags that sit beside a name. */
export function Tag({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("inline-flex shrink-0 items-center rounded-full bg-deodar-tint px-[7px] py-px font-mono text-[9.5px] uppercase tracking-[.08em] text-deodar", className)}>{children}</span>;
}

const statusTone: Record<BookingStatus, PillTone> = {
  hold: "brass",
  confirmed: "pine",
  checked_in: "deodar",
  checked_out: "slate",
  cancelled: "subtle",
  expired: "subtle",
};

export function StatusPill({ status, unacknowledged, className }: { status: BookingStatus; unacknowledged?: boolean; className?: string }) {
  if (unacknowledged) return <Pill tone="rhodo" className={className}>Unconfirmed</Pill>;
  return (
    <Pill tone={statusTone[status]} className={className}>
      {STATUS_LABEL[status]}
    </Pill>
  );
}

/** Live MM:SS for a hold. */
export function Countdown({ until, className }: { until: string; className?: string }) {
  const now = useNow();
  const left = new Date(until).getTime() - now;
  return <span className={cn("font-mono", left < 10 * 60_000 && "text-rhodo", className)}>{left > 0 ? fmtCountdown(left) : "0:00"}</span>;
}

/* ───────────────────────────── Surfaces ───────────────────────────── */

/**
 * One line, not a title page. This used to open every screen with a 46px
 * display heading and a lede, around 180px before the first row of anything
 * useful — on a phone that is most of the fold spent saying where you already
 * know you are. The page is named once, quietly, and the work starts.
 *
 * The lede survives as a hint alongside, where there is width for it: it is
 * the only place some gestures are written down.
 */
/**
 * Declares the screen: its number in the course bar, its name, and a line on
 * how it works. Nothing is drawn here — the top bar shows the name, and the
 * line opens from its ⓘ — so every screen starts with its content.
 */
export function PageHeader({
  index,
  eyebrow,
  kicker,
  lede,
  eyebrowTone,
}: {
  index?: string;
  eyebrow: string;
  kicker?: string;
  lede?: ReactNode;
  eyebrowTone?: "rhodo";
}) {
  const setMeta = usePageMeta((s) => s.set);
  useEffect(() => {
    setMeta({ index, eyebrow, kicker, lede, tone: eyebrowTone });
  }, [setMeta, index, eyebrow, kicker, lede, eyebrowTone]);
  useEffect(() => () => setMeta(null), [setMeta]);
  return null;
}

/** A screen that opens with its own name in the page: eyebrow, Fraunces title, a line under it. */
export function PageTitle({ eyebrow, title, lede }: { eyebrow?: ReactNode; title: ReactNode; lede?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      {eyebrow && <div className="font-mono text-[10px] uppercase tracking-[.16em] text-subtle">{eyebrow}</div>}
      <h1 className="font-display text-[clamp(28px,4vw,36px)] font-semibold leading-[1.1] tracking-[-.02em]">{title}</h1>
      {lede && <p className="max-w-[640px] text-[15px] text-pretty text-muted">{lede}</p>}
    </div>
  );
}

/** The narrower column Today and Settings sit in. */
export function Column({ children, className }: { children: ReactNode; className?: string }) {
  const rail = useInRail();
  return (
    <div className={cn(rail ? "mr-auto" : "mx-auto", "flex w-full max-w-[1240px] flex-col gap-[22px] px-3.5 pb-7 pt-4 min-[900px]:px-6 min-[900px]:pb-12 min-[900px]:pt-7", className)}>
      {children}
    </div>
  );
}

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  const rail = useInRail();
  return <div className={cn(rail ? "mr-auto" : "mx-auto", "w-full max-w-[1440px] px-4 py-6 sm:px-8 sm:py-7", className)}>{children}</div>;
}

export function Panel({
  eyebrow,
  title,
  actions,
  children,
  className,
  bodyClassName,
  tone,
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  tone?: "sunk";
}) {
  return (
    <section className={cn("overflow-hidden rounded-lg border border-line-strong", tone === "sunk" ? "bg-sunk" : "bg-surface", className)}>
      {(eyebrow || title || actions) && (
        <div className="flex min-h-[46px] flex-wrap items-center gap-3 border-b border-line bg-sunk px-4 py-2.5 sm:px-[18px]">
          <div className="min-w-0 flex-1">
            {eyebrow && <Eyebrow className="!text-muted">{eyebrow}</Eyebrow>}
            {title && <div className="font-display text-[21px] font-semibold leading-tight">{title}</div>}
          </div>
          {actions}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/** Label / value rows in alternating courses. */
export function Facts({ rows, className }: { rows: ([ReactNode, ReactNode] | [ReactNode, ReactNode, string])[]; className?: string }) {
  return (
    <dl className={cn("flex flex-col", className)}>
      {rows.map(([k, v, cls], i) => (
        <div key={i} className={cn("flex items-center justify-between gap-4 border-b border-line px-4 py-3 last:border-b-0 sm:px-6", i % 2 ? "bg-sunk" : "bg-surface")}>
          <dt className="text-[14px] text-muted">{k}</dt>
          <dd className={cn("text-right", cls)}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon,
  className,
}: {
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  /** A 26px Solar icon for the well above the title. */
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center gap-2 rounded-lg border border-line bg-surface px-5 py-10 text-center", className)}>
      <span aria-hidden className="mb-1.5 flex size-[52px] items-center justify-center rounded-full bg-sunk text-subtle">
        {icon ?? <InboxIcon size={26} />}
      </span>
      <div className="font-display text-[22px] font-medium">{title}</div>
      {body && <div className="mx-auto max-w-[52ch] text-[14.5px] leading-[1.55] text-muted">{body}</div>}
      {action && <div className="mt-2.5 flex justify-center">{action}</div>}
    </div>
  );
}

export function ErrorState({ title = "Couldn't load this", body, onRetry, className }: { title?: string; body?: ReactNode; onRetry?: () => void; className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-start gap-3.5 rounded-md border border-[color-mix(in_srgb,var(--rhododendron)_40%,transparent)] bg-[color-mix(in_srgb,var(--rhododendron)_8%,var(--surface))] px-[18px] py-4",
        className,
      )}
    >
      <span className="rounded-full bg-rhodo px-2 py-[3px] font-mono text-[10.5px] tracking-[.08em] text-[#FFFDF8]">ERROR</span>
      <div className="min-w-[200px] flex-1">
        <div className="text-[15px] font-semibold">{title}</div>
        {body && <div className="mt-0.5 text-[13.5px] text-muted">{body}</div>}
      </div>
      {onRetry && (
        <Button size="sm" className="bg-surface" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Skeleton({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-2 p-4", className)} aria-busy="true" aria-label="Loading">
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="shimmer h-3.5 rounded-full" style={{ width: `${[100, 72, 48, 86, 60][i % 5]}%` }} />
      ))}
    </div>
  );
}

type NoticeTone = "pine" | "brass" | "rhodo" | "slate" | "deodar" | "mmt";
const noticeTone: Record<NoticeTone, { chip: string; ground: string }> = {
  pine: { chip: "bg-pine text-[#FFFDF8]", ground: "bg-[color-mix(in_srgb,var(--pine)_9%,var(--surface))]" },
  brass: { chip: "bg-brass text-brass-ink", ground: "bg-[color-mix(in_srgb,var(--brass)_15%,var(--surface))]" },
  rhodo: { chip: "bg-rhodo text-[#FFFDF8]", ground: "bg-[color-mix(in_srgb,var(--rhododendron)_9%,var(--surface))]" },
  slate: { chip: "bg-slate text-on-fill", ground: "bg-[color-mix(in_srgb,var(--slate)_10%,var(--surface))]" },
  deodar: { chip: "bg-deodar text-on-fill", ground: "bg-deodar-tint" },
  mmt: { chip: "bg-ch-mmt text-[#FFFDF8]", ground: "bg-[color-mix(in_srgb,var(--ch-mmt)_10%,var(--surface))]" },
};

/** A tagged line on a tinted ground: FREE / TAKEN / BUFFER / ! */
export function Notice({ tone, tag, children, className }: { tone: NoticeTone; tag: string; children: ReactNode; className?: string }) {
  const t = noticeTone[tone];
  return (
    <div className={cn("flex items-start gap-2.5 rounded-md px-3.5 py-[11px]", t.ground, className)}>
      <span className={cn("mt-px shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px]", t.chip)}>{tag}</span>
      <div className="min-w-0 text-[13.5px] leading-[1.5]">{children}</div>
    </div>
  );
}

/* ───────────────────────────── Form controls ───────────────────────────── */

export function Label({ children, htmlFor, hint }: { children: ReactNode; htmlFor?: string; hint?: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 flex items-baseline justify-between gap-2 text-[13px] text-muted">
      <span>{children}</span>
      {hint && <span className="text-[12px] text-subtle">{hint}</span>}
    </label>
  );
}

const fieldBase =
  "w-full rounded-md border border-line-strong bg-sunk px-3 text-[16px] text-ink placeholder:text-subtle focus:border-deodar focus:bg-surface focus:outline-none focus:ring-soft disabled:opacity-60 aria-[invalid=true]:border-rhodo aria-[invalid=true]:bg-[color-mix(in_srgb,var(--rhodo)_6%,var(--surface))]";

/* ───────────────────────────── Validation ───────────────────────────── */

/** Set by <Field> for the control inside it: its id, and the message it is failing with. */
const FieldContext = createContext<{ errorId?: string; invalid: boolean }>({ invalid: false });

/** The aria wiring every house control spreads onto its element. */
function useFieldAria(): { "aria-invalid"?: true; "aria-describedby"?: string } {
  const f = useContext(FieldContext);
  return f.invalid ? { "aria-invalid": true, "aria-describedby": f.errorId } : {};
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { mono?: boolean; big?: boolean }>(function Input(
  { className, mono, big, ...rest },
  ref,
) {
  return <input ref={ref} className={cn(fieldBase, big ? "min-h-[56px] text-[18px]" : "min-h-[52px]", mono && "font-mono", className)} {...useFieldAria()} {...rest} />;
});

/**
 * A phone number with its country apart: a flag-and-code button that opens a
 * searchable list, then the number itself. The value stays one string —
 * "+44 7911 123456" — so drafts, the outbox and the API see what they always
 * did. Typing or pasting a number that starts with "+" picks its country.
 */
export const PhoneField = forwardRef<
  HTMLInputElement,
  { value: string; onChange: (v: string) => void; onBlur?: () => void; id?: string; placeholder?: string; big?: boolean; className?: string; defaultCountry?: string; label?: string }
>(function PhoneField({ value, onChange, onBlur, id, placeholder, big, className, defaultCountry = DEFAULT_COUNTRY.iso, label = "Phone number" }, ref) {
  const aria = useFieldAria();
  // The country chosen while the number is still empty; once there is one, the number says which it is.
  const [picked, setPicked] = useState(defaultCountry);
  const parsed = splitPhone(value, picked);
  const country = parsed?.country ?? countryByIso(picked);
  const national = parsed ? parsed.national : value;

  // A trunk 0 is never dialled after a country code (Italy keeps its own).
  const emit = (c: Country, text: string) => {
    const n = c.iso === "IT" ? text : text.replace(/^0+/, "");
    onChange(n.trim() ? `+${c.dial} ${n}` : "");
  };
  const typed = (text: string) => {
    if (text.trim().startsWith("+")) {
      const hit = splitPhone(text, picked);
      if (hit) setPicked(hit.country.iso);
      return onChange(text);
    }
    emit(country, text.replace(/[^\d\s-]/g, ""));
  };
  const choose = (c: Country) => {
    setPicked(c.iso);
    if (national.trim()) emit(c, national);
  };

  return (
    <div
      className={cn(
        "relative flex rounded-md border border-line-strong bg-sunk focus-within:border-deodar focus-within:bg-surface focus-within:ring-soft",
        aria["aria-invalid"] && "border-rhodo",
        big ? "min-h-[56px]" : "min-h-[52px]",
        className,
      )}
    >
      <CountryPicker country={country} onPick={choose} big={big} />
      <input
        ref={ref}
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="off"
        aria-label={id ? undefined : label}
        {...aria}
        value={national}
        onChange={(e) => typed(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder ?? (country.iso === "IN" ? "98110 44821" : "Number")}
        className={cn("min-w-0 flex-1 rounded-r-md bg-transparent px-3 font-mono text-ink placeholder:text-subtle focus:outline-none", big ? "text-[19px]" : "text-[16px]")}
      />
    </div>
  );
});

function CountryPicker({ country, onPick, big }: { country: Country; onPick: (c: Country) => void; big?: boolean }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const term = q.trim().toLowerCase().replace(/^\+/, "");
  const list = term ? COUNTRIES.filter((c) => c.name.toLowerCase().includes(term) || c.iso.toLowerCase() === term || c.dial.startsWith(term)) : COUNTRIES;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    if (open) listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const show = () => {
    setQ("");
    setActive(Math.max(0, COUNTRIES.indexOf(country)));
    setOpen(true);
  };
  const hide = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };
  const pick = (c: Country | undefined) => {
    if (!c) return;
    onPick(c);
    hide();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      hide();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(list.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(list[active]);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative flex">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Country: ${country.name} +${country.dial}`}
        onClick={() => (open ? hide() : show())}
        className={cn(
          "flex shrink-0 items-center gap-1.5 rounded-l-md border-r border-line pl-3 pr-2 hover:bg-hover-tint focus-visible:bg-hover-tint focus-visible:outline-none",
          open && "bg-sel-tint",
        )}
      >
        <span aria-hidden className={cn("leading-none", big ? "text-[22px]" : "text-[19px]")}>
          {flagOf(country.iso)}
        </span>
        <span className={cn("font-mono font-semibold text-ink", big ? "text-[17px]" : "text-[15px]")}>+{country.dial}</span>
        <AltArrowDownIcon size={14} className={cn("text-muted transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="animate-pop-k absolute left-0 top-[calc(100%+6px)] z-50 w-[min(300px,calc(100vw-32px))] overflow-hidden rounded-lg border border-line-strong bg-surface shadow-float" onKeyDown={onKeyDown}>
          <div className="border-b border-line p-[5px]">
            <input
              autoFocus
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setActive(0);
              }}
              placeholder="Country or code"
              aria-label="Search countries"
              aria-controls={listId}
              aria-activedescendant={list[active] ? `${listId}-${list[active].iso}` : undefined}
              className="h-10 w-full rounded-[calc(var(--r-lg)-6px)] bg-sunk px-3 text-[15px] text-ink placeholder:text-subtle focus:outline-none"
            />
          </div>
          <div ref={listRef} id={listId} role="listbox" aria-label="Country" className="max-h-[272px] overflow-y-auto p-[5px]">
            {list.length === 0 && <div className="px-3 py-3 text-[13.5px] text-subtle">No country by that name or code.</div>}
            {list.map((c, i) => {
              const on = c.iso === country.iso;
              return (
                <div
                  key={c.iso}
                  id={`${listId}-${c.iso}`}
                  role="option"
                  aria-selected={on}
                  data-active={i === active}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(c)}
                  className={cn("flex min-h-[42px] cursor-pointer items-center gap-3 rounded-[calc(var(--r-lg)-6px)] px-3 text-[14.5px]", on ? "bg-deodar-tint font-semibold" : i === active && "bg-sunk")}
                >
                  <span aria-hidden className="text-[19px] leading-none">
                    {flagOf(c.iso)}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  <span className="shrink-0 font-mono text-[13px] font-normal text-muted">+{c.dial}</span>
                  {on && <CheckCircleBoldIcon size={18} className="shrink-0 text-deodar" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** A field with a fixed lead: +91 before a phone, ₹ before an amount. */
export function PrefixField({ prefix, children, className }: { prefix: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex overflow-hidden rounded-md border border-line-strong focus-within:border-deodar focus-within:ring-soft [&_input]:rounded-none [&_input]:border-0 [&_input]:focus:ring-0",
        className,
      )}
    >
      <span className="flex shrink-0 items-center border-r border-line bg-surface px-3 font-mono text-[14px] text-muted">{prefix}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(fieldBase, "min-h-[96px] resize-y py-2.5 text-[14.5px] leading-[1.5]", className)} {...useFieldAria()} {...rest} />;
});

/**
 * The select, built here rather than borrowed from the browser: a trigger in
 * the field style and a listbox laid as courses, so it carries the same
 * border, the same Plex numerals and the same deodar focus rule as every
 * other control. A native select rides along underneath, sized to the
 * trigger and invisible, purely so `required`, `name` and form reset keep
 * working — the browser's validation bubble needs something real to point at.
 *
 * Keyboard: Enter, Space or either arrow opens; arrows move; Enter or Space
 * picks; Escape closes; letters jump to a label.
 */
export type SelectOption = { value: string; label: string; note?: string; disabled?: boolean };

export function Select({
  options,
  value,
  onChange,
  placeholder = "Choose…",
  id,
  name,
  required,
  disabled,
  className,
  align = "left",
  label,
}: {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  align?: "left" | "right";
  /** Accessible name when no <Field> label points at this one. */
  label?: string;
}) {
  const aria = useFieldAria();
  const [open, setOpen] = useState(false);
  /** Index into `options` of the highlighted row. */
  const [active, setActive] = useState(-1);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const typed = useRef({ buffer: "", at: 0 });
  const listId = useId();

  const selected = options.findIndex((o) => o.value === value);
  const current = selected >= 0 ? options[selected] : null;
  // A long list gets a search field; the rows below it follow what is typed.
  const searchable = options.length > 7;
  const matching = (q: string) => options.map((o, i) => ({ o, i })).filter(({ o }) => !q.trim() || o.label.toLowerCase().includes(q.trim().toLowerCase()));
  const shown = matching(query);

  const close = useCallback(() => setOpen(false), []);
  useLayer(open, triggerRef, layerRef, close, { align, minWidth: 220, matchWidth: true, maxHeight: 320, measure: shown.length });

  // Keep the highlighted row in view by moving the list, never the page.
  useEffect(() => {
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>('[data-active="true"]');
    if (!open || !list || !row) return;
    if (row.offsetTop < list.scrollTop) list.scrollTop = row.offsetTop - 5;
    else if (row.offsetTop + row.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = row.offsetTop + row.offsetHeight - list.clientHeight + 5;
  }, [open, active, query]);

  /** Next enabled option after `from` in direction `dir`, among the rows shown; `from` if there is none. */
  const step = (from: number, dir: 1 | -1, rows = shown) => {
    let k = from < 0 ? -1 : from >= options.length ? rows.length : rows.findIndex((r) => r.i === from);
    if (k === -1 && from >= 0 && from < options.length) k = dir === 1 ? -1 : rows.length;
    for (k += dir; k >= 0 && k < rows.length; k += dir) if (!rows[k].o.disabled) return rows[k].i;
    return from;
  };
  const show = () => {
    if (disabled) return;
    setQuery("");
    setActive(selected >= 0 ? selected : step(-1, 1, matching("")));
    setOpen(true);
  };
  const hide = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };
  const pick = (i: number) => {
    const o = options[i];
    if (!o || o.disabled) return;
    onChange(o.value);
    hide();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        show();
      }
      return;
    }
    // In the search field, letters, space, Home and End edit the text.
    const inSearch = e.target === searchRef.current;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      hide();
    } else if (e.key === "Tab") {
      setOpen(false);
    } else if (e.key === "Enter" || (e.key === " " && !inSearch)) {
      e.preventDefault();
      pick(active);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => step(a, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => step(a, -1));
    } else if (inSearch) {
      return;
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(step(-1, 1));
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(step(options.length, -1));
    } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      typed.current.buffer = e.timeStamp - typed.current.at > 700 ? e.key : typed.current.buffer + e.key;
      typed.current.at = e.timeStamp;
      const q = typed.current.buffer.toLowerCase();
      const hit = options.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(q));
      if (hit >= 0) setActive(hit);
    }
  };

  const activeId = open && active >= 0 ? `${listId}-${active}` : undefined;

  return (
    <div ref={wrapRef} className="relative" onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={activeId}
        aria-label={label}
        disabled={disabled}
        onClick={() => (open ? hide() : show())}
        {...aria}
        className={cn(fieldBase, "flex min-h-[48px] items-center gap-2 pr-3 text-left text-[15px]", open && "border-deodar bg-surface ring-soft", className)}
      >
        <span className={cn("min-w-0 flex-1 truncate", !current && "text-subtle")}>{current ? current.label : placeholder}</span>
        {current?.note && <span className="shrink-0 text-[13px] text-subtle">{current.note}</span>}
        <AltArrowDownIcon size={16} className={cn("shrink-0 text-muted transition-transform", open && "rotate-180")} />
      </button>

      {/* Value carrier: keeps native validation, name and form reset. Never focusable, never clickable. */}
      <select
        tabIndex={-1}
        aria-hidden
        name={name}
        required={required}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pointer-events-none absolute inset-0 m-0 size-full opacity-0"
      >
        <option value="" />
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {/* In <body>, fixed: no overflow-hidden ancestor (a Sheet, a card) can cut it off. Events still bubble to this wrapper. */}
      {open &&
        createPortal(
          <div
            ref={layerRef}
            {...{ [LAYER_ATTR]: "" }}
            className="animate-pop-k invisible fixed left-0 top-0 z-[85] flex flex-col overflow-hidden rounded-lg border border-line-strong bg-surface shadow-float"
          >
            {searchable && (
              <div className="shrink-0 border-b border-line p-[5px]">
                <input
                  ref={searchRef}
                  // Only with a mouse: on a phone, focusing it would throw up the keyboard over the list.
                  autoFocus={finePointer()}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(step(-1, 1, matching(e.target.value)));
                  }}
                  placeholder="Search…"
                  aria-label={`Search ${label ?? "options"}`}
                  aria-controls={listId}
                  aria-activedescendant={activeId}
                  className="h-10 w-full rounded-sm bg-sunk px-3 text-[14.5px] text-ink outline-none placeholder:text-subtle focus:ring-soft"
                />
              </div>
            )}
            <div ref={listRef} id={listId} role="listbox" aria-label={label} className="relative min-h-0 flex-1 overflow-y-auto p-[5px]">
              {options.length === 0 && <div className="px-3 py-3 text-[13.5px] text-subtle">Nothing to choose from yet.</div>}
              {options.length > 0 && shown.length === 0 && <div className="px-3 py-3 text-[13.5px] text-subtle">No match</div>}
              {shown.map(({ o, i }) => (
                <div
                  key={o.value}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={o.value === value}
                  aria-disabled={o.disabled}
                  data-active={i === active}
                  onMouseEnter={() => !o.disabled && setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(i)}
                  className={cn(
                    "flex min-h-[42px] cursor-pointer items-center gap-3 rounded-sm px-3 text-[14.5px]",
                    o.value === value ? "bg-deodar-tint font-semibold" : i === active && "bg-sunk",
                    o.value === value && i === active && "brightness-[.97]",
                    o.disabled && "cursor-not-allowed opacity-55",
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.note && <span className="shrink-0 text-[13px] font-normal text-subtle">{o.note}</span>}
                  {o.value === value && <CheckCircleBoldIcon size={18} className="shrink-0 text-deodar" />}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

/** "13:30" → "1:30 pm", the hint beside each 24-hour time. */
function twelveHour(t: string): string {
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
}

/** Every half hour of the day, 00:00 to 23:30. */
const HALF_HOURS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);

/**
 * A time of day, "HH:MM" on the 24-hour clock — check-in and check-out —
 * chosen from the house listbox rather than the browser's time input, which
 * looks different on every phone. Half-hour steps; a stored time off the grid
 * (13:15) stays selectable. Type "14" to jump to 14:00.
 */
export function TimeField({
  value,
  onChange,
  id,
  label,
  className,
  compact,
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  label?: string;
  className?: string;
  /** Inline in a table row rather than a full-width form field. */
  compact?: boolean;
}) {
  const times = value && !HALF_HOURS.includes(value) ? [...HALF_HOURS, value].sort() : HALF_HOURS;
  return (
    <Select
      id={id}
      label={label}
      value={value}
      onChange={onChange}
      placeholder="--:--"
      options={times.map((t) => ({ value: t, label: t, note: twelveHour(t) }))}
      className={cn("font-mono", compact && "!min-h-9 !px-2 !text-[14px]", className)}
    />
  );
}

/**
 * A labelled control. Its error comes from `error`, or — given a `name` — from
 * the enclosing form's errors (FormErrorsContext). Failing, it shows the
 * message beneath and the control inside turns red and says why to a screen
 * reader; no control has to be told.
 */
export function Field({
  label,
  hint,
  error,
  name,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  /** The API's path for this value, to pick its message out of the form's errors. */
  name?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const form = useContext(FormErrorsContext);
  const register = form.register;
  useEffect(() => (name && register ? register(name) : undefined), [name, register]);
  // A list's field ("rooms") also owns its items' errors ("rooms.3.name").
  const message = error ?? (name ? (form.errors[name] ?? Object.entries(form.errors).find(([p]) => p.startsWith(`${name}.`))?.[1]) : undefined);
  return (
    <div>
      <Label htmlFor={id} hint={hint}>
        {label}
      </Label>
      <FieldContext.Provider value={{ errorId, invalid: !!message }}>{children(id)}</FieldContext.Provider>
      {message && (
        <div id={errorId} role="alert" className="mt-1.5 flex items-start gap-1.5 text-[13px] leading-[1.45] text-rhodo">
          <span aria-hidden className="mt-[5px] size-1.5 shrink-0 rounded-full bg-rhodo" />
          {message}
        </div>
      )}
    </div>
  );
}

/** The 20px box on its own: a CSS tick when checked, a bar when some-but-not-all. */
export function CheckMark({ checked, indeterminate, className }: { checked: boolean; indeterminate?: boolean; className?: string }) {
  const on = checked || indeterminate;
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded-xs border-[1.5px] transition-colors duration-100",
        on ? "border-deodar bg-deodar" : "border-line-strong bg-surface",
        className,
      )}
    >
      {checked && !indeterminate && <span className="h-[9px] w-[5px] -translate-y-px rotate-45 border-b-2 border-r-2 border-on-fill" />}
      {indeterminate && <span className="h-0.5 w-[9px] rounded-[2px] bg-on-fill" />}
    </span>
  );
}

export function Checkbox({
  checked,
  onChange,
  indeterminate,
  children,
  className,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  indeterminate?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("flex min-h-[44px] cursor-pointer items-center gap-2.5 text-[14.5px]", className)}>
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        ref={(el) => {
          if (el) el.indeterminate = !!indeterminate;
        }}
        onChange={(e) => onChange(e.target.checked)}
      />
      <CheckMark checked={checked} indeterminate={indeterminate} className="peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-deodar" />
      <span className="min-w-0 flex-1">{children}</span>
    </label>
  );
}

/** One choice from a few, each a card: payment method (UPI, Cash, Card). */
export function RadioCards<T extends string>({
  options,
  value,
  onChange,
  name,
  className,
}: {
  options: { value: T; label: ReactNode; disabled?: boolean }[];
  value: T | null;
  onChange: (v: T) => void;
  name: string;
  className?: string;
}) {
  return (
    <div role="radiogroup" className={cn("flex flex-wrap gap-2", className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <label
            key={o.value}
            className={cn(
              "flex min-h-11 flex-[1_1_0] cursor-pointer items-center gap-2.5 rounded-md px-3 text-[14.5px] transition-colors duration-100 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-deodar",
              on ? "border-[1.5px] border-deodar bg-deodar-tint font-semibold" : "border border-line-strong bg-surface hover:bg-sunk",
              o.disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <input type="radio" className="sr-only" name={name} checked={on} disabled={o.disabled} onChange={() => onChange(o.value)} />
            <span aria-hidden className={cn("size-[18px] shrink-0 rounded-full bg-surface", on ? "border-[5.5px] border-deodar" : "border-[1.5px] border-line-strong")} />
            <span className="min-w-0 flex-1">{o.label}</span>
          </label>
        );
      })}
    </div>
  );
}

/** An on/off setting. The whole row is the switch. */
export function Switch({ checked, onChange, label, hint, disabled, className }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; hint?: ReactNode; disabled?: boolean; className?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn("flex min-h-11 w-full items-center gap-3 text-left disabled:cursor-not-allowed disabled:opacity-50", className)}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[14.5px]">{label}</span>
        {hint && <span className="block text-[12.5px] text-subtle">{hint}</span>}
      </span>
      <span aria-hidden className={cn("relative h-[26px] w-11 shrink-0 rounded-full transition-colors duration-150", checked ? "bg-deodar" : "bg-line-strong")}>
        <span
          className={cn("absolute top-[3px] size-5 rounded-full bg-surface shadow-[0_1px_3px_rgba(13,16,20,.3)] transition-[left] duration-150", checked ? "left-[21px]" : "left-[3px]")}
        />
      </span>
    </button>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  className,
  size = "md",
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
  size?: "sm" | "md";
}) {
  return (
    // The chosen segment wears the house "selected" mark — deodar-tinted, with a
    // deodar edge — the same as a chosen room or meal plan, not a black block.
    <div role="tablist" className={cn("inline-flex gap-[2px] rounded-md border border-line-strong bg-sunk p-[3px]", className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            className={cn(
              "relative inline-flex items-center justify-center gap-1.5 rounded-[calc(var(--r-md)-3px)] px-3.5 font-semibold transition-colors duration-100",
              size === "sm" ? "min-h-[34px] text-[13px]" : "min-h-9 text-[13.5px]",
              on ? "bg-deodar-tint text-ink shadow-[inset_0_0_0_1.5px_var(--deodar)]" : "text-muted hover:bg-surface hover:text-ink",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Tabs<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: { value: T; label: ReactNode; count?: number }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
}) {
  return (
    // Pulled 1px over the parent's rule, so the active bar sits on it.
    <div role="tablist" className={cn("no-scrollbar -mb-px flex items-end gap-1 overflow-x-auto", className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            className={cn("relative inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap px-3 text-[14px]", on ? "font-semibold text-ink" : "text-muted hover:text-ink")}
          >
            {o.label}
            {o.count !== undefined && (
              <span
                className={cn(
                  "inline-flex h-5 min-w-[22px] items-center justify-center rounded-full px-1.5 font-mono text-[11px] font-normal",
                  on ? "bg-deodar text-on-fill" : "bg-sunk text-subtle",
                )}
              >
                {o.count}
              </span>
            )}
            {on && <span aria-hidden className="absolute inset-x-3 bottom-0 h-[2.5px] rounded-t-[3px] bg-deodar" />}
          </button>
        );
      })}
    </div>
  );
}

/**
 * −/+ stepper around a date. Tapping the date opens the native picker, so
 * any date is two taps away while the common ±1 day is one.
 */
export function DateStepper({
  label,
  value,
  onChange,
  min,
  max,
  size = "md",
  timezone,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
  size?: "md" | "lg";
  timezone?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const canDown = !min || addDays(value, -1) >= min;
  const canUp = !max || addDays(value, 1) <= max;
  const btn = cn("flex shrink-0 items-center justify-center rounded-full border border-line bg-sunk text-ink hover:bg-canvas disabled:opacity-35", size === "lg" ? "size-10" : "size-[34px]");

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div ref={ref} className="relative min-w-0 flex-1 bg-surface px-2 py-1.5">
      <div className="font-mono text-[9px] uppercase tracking-[.12em] text-subtle">{label}</div>
      <div className="flex items-center justify-between gap-1">
        <button type="button" className={btn} disabled={!canDown} onClick={() => onChange(addDays(value, -1))} aria-label={`${label} one day earlier`}>
          <MinusIcon size={16} />
        </button>
        {/* ±1 day is one tap; any other date is two. */}
        <button type="button" className="min-w-0 flex-1 text-center" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={`Pick ${label.toLowerCase()} date`}>
          <span className={cn("block font-mono font-semibold leading-tight", size === "lg" ? "text-[16px]" : "text-[15px]")}>{fmtShort(value)}</span>
          <span className="block font-mono text-[10px] text-subtle">{weekdayShort(value)}</span>
        </button>
        <button type="button" className={btn} disabled={!canUp} onClick={() => onChange(addDays(value, 1))} aria-label={`${label} one day later`}>
          <AddCircleIcon size={16} />
        </button>
      </div>
      {open && (
        <div className="animate-pop-k absolute left-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-lg border border-line-strong bg-surface shadow-float">
          <Calendar
            value={value}
            min={min}
            max={max}
            timezone={timezone}
            onPick={(d) => {
              onChange(d);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Steppers side by side (check-in / check-out, adults / children), split by
 * 1px rules. Not clipped: a stepper's calendar hangs out below it, so the
 * outer children round their own corners instead.
 */
export function StepperGroup({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex gap-px rounded-md border border-line-strong bg-line [&>*:first-child]:rounded-l-[calc(var(--r-md)-1px)] [&>*:last-child]:rounded-r-[calc(var(--r-md)-1px)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function RangeNav({ label, onPrev, onNext, onToday }: { label: ReactNode; onPrev: () => void; onNext: () => void; onToday?: () => void }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-line-strong bg-surface">
      <button className="flex min-h-[44px] items-center border-r border-line px-3 hover:bg-sunk" onClick={onPrev} aria-label="Earlier">
        <AltArrowLeftIcon size={18} />
      </button>
      <span className="flex min-h-[44px] items-center px-4 font-mono text-[13px]">{label}</span>
      {onToday && (
        <button className="flex min-h-[44px] items-center border-l border-line px-3 font-mono text-[11px] uppercase tracking-[.1em] hover:bg-sunk" onClick={onToday}>
          Today
        </button>
      )}
      <button className="flex min-h-[44px] items-center border-l border-line px-3 hover:bg-sunk" onClick={onNext} aria-label="Later">
        <AltArrowRightIcon size={18} />
      </button>
    </div>
  );
}

/** Staff never see money. Render nothing at all when a figure is absent. */
export function MaybeMoney({ value, children }: { value: number | undefined; children: (v: number) => ReactNode }) {
  return value === undefined ? null : <>{children(value)}</>;
}

/**
 * A row of 38px pill chips, one picked: where a lost item was found, an
 * Activity area. Each may carry a count. The picked one wears the house
 * "selected" mark, deodar-tinted with a deodar edge.
 */
export function ChipGroup<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: { value: T; label: ReactNode; count?: number }[];
  value: T | null;
  onChange: (v: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cn("flex flex-wrap gap-1.5", className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex h-[38px] items-center gap-1.5 rounded-full border px-3.5 text-[14px]",
              on ? "border-deodar bg-deodar-tint font-semibold text-ink" : "border-line-strong bg-surface text-muted hover:text-ink",
            )}
          >
            {o.label}
            {o.count !== undefined && <span className="font-mono text-[11.5px] text-subtle">{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
